import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRight, ArrowUpRight, X, ArrowLeft, Menu } from "lucide-react";
import docsData from "./Docs.json";

// ─── Sidebar Section Item ────────────────────────────────────────────────────
const SidebarSection = ({ section, activeId, onSelect }) => {
  const isExpanded =
    activeId === section.id ||
    section.subsections.some((s) => s.id === activeId);

  return (
    <div className="mb-1">
      <button
        onClick={() => onSelect(section.id)}
        className={`w-full text-left px-3 py-2 rounded-md text-sm uppercase font-[Norwester] transition-colors duration-150
          ${isExpanded ? "text-white" : "text-gray-500 hover:text-gray-300"}`}
      >
        <span className="flex items-center gap-1">
          <ChevronRight
            size={13}
            className={`transition-transform duration-200 flex-shrink-0 ${
              isExpanded ? "rotate-90 text-white" : "text-gray-600"
            }`}
          />
          {section.title}
        </span>
      </button>

      {isExpanded && (
        <div className="ml-4 mt-0.5 border-l border-gray-800 pl-3 space-y-0.5">
          {section.subsections.map((sub) => (
            <button
              key={sub.id}
              onClick={() => onSelect(sub.id)}
              className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors duration-150 flex items-center gap-1.5
                ${
                  activeId === sub.id
                    ? "text-[#FFF528] font-normal"
                    : "text-gray-500 hover:text-gray-300 font-normal"
                }`}
            >
              {activeId === sub.id && (
                <span className="w-1 h-1 rounded-full bg-[#FFF528] flex-shrink-0" />
              )}
              {sub.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Content Renderer ────────────────────────────────────────────────────────
const renderContent = (text) => {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let listItems = [];
  let numberedItems = [];

  const flushList = () => {
    if (listItems.length) {
      elements.push(
        <ul key={`ul-${elements.length}`} className="mt-2 space-y-1">
          {listItems.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-gray-300 text-sm"
            >
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FFF528] flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>,
      );
      listItems = [];
    }
    if (numberedItems.length) {
      elements.push(
        <ol key={`ol-${elements.length}`} className="mt-2 space-y-1">
          {numberedItems.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-gray-300 text-sm"
            >
              <span className="text-[#FFF528] font-semibold flex-shrink-0 min-w-[16px]">
                {i + 1}.
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ol>,
      );
      numberedItems = [];
    }
  };

  lines.forEach((line, idx) => {
    if (line.startsWith("- ")) {
      listItems.push(line.slice(2));
    } else if (/^\d+\.\s/.test(line)) {
      flushList();
      numberedItems.push(line.replace(/^\d+\.\s/, ""));
    } else {
      flushList();
      if (line.trim() === "") {
        elements.push(<div key={idx} className="h-2" />);
      } else {
        elements.push(
          <p key={idx} className="text-gray-300 text-sm leading-relaxed">
            {line}
          </p>,
        );
      }
    }
  });

  flushList();
  return elements;
};

// ─── Main DocsPage ───────────────────────────────────────────────────────────
const DocsPage = () => {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(docsData[0].subsections[0].id);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sectionRefs = useRef({});
  const contentRef = useRef(null);
  const isScrollingRef = useRef(false);
  const sidebarRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 967);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 967);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close sidebar on outside click (mobile)
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        sidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [sidebarOpen]);

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (!el || !contentRef.current) return;
    isScrollingRef.current = true;
    setActiveId(id);
    setSidebarOpen(false); // close drawer on mobile after selection
    const offset = el.offsetTop - 32;
    contentRef.current.scrollTo({ top: offset, behavior: "smooth" });
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 800);
  };

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const onScroll = () => {
      if (isScrollingRef.current) return;
      const scrollTop = container.scrollTop;

      let closest = null;
      let closestDist = Infinity;

      Object.entries(sectionRefs.current).forEach(([id, el]) => {
        if (!el) return;
        const dist = Math.abs(el.offsetTop - scrollTop - 32);
        if (dist < closestDist) {
          closestDist = dist;
          closest = id;
        }
      });

      if (closest && closest !== activeId) setActiveId(closest);
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [activeId]);

  // ── Sidebar inner content (shared between desktop & mobile drawer) ──
  const SidebarContent = () => (
    <>
      {/* Header */}
      <div className="px-4 py-5 border-b border-gray-800/60 flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-white transition-colors flex-shrink-0"
          aria-label="Go back"
        >
          <ArrowLeft size={16} />
        </button>
        <p className="text-sm text-gray-500 font-semibold">Sections</p>

        {/* Close button — only visible on mobile */}
        {!isDesktop && (
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto text-gray-500 hover:text-white transition-colors"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1 scrollbar-thin scrollbar-thumb-gray-800">
        {docsData.map((section) => (
          <SidebarSection
            key={section.id}
            section={section}
            activeId={activeId}
            onSelect={scrollTo}
          />
        ))}
      </nav>

      {/* Close Docs footer */}
      {/* <div className="px-4 py-4 border-t border-gray-800/60 flex-shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-xs text-gray-500 hover:text-white transition-colors"
        >
          <X size={13} />
          Close Docs
        </button>
      </div> */}
    </>
  );

  return (
    <div
      className="flex bg-[#0a0a0a] text-white font-sans relative"
      style={{ height: "100vh", overflow: "hidden" }}
    >
      {/* ── Mobile overlay backdrop ── */}
      {sidebarOpen && !isDesktop && (
        <div
          className="fixed inset-0 bg-black/60 z-20"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Desktop Sidebar ── */}
      {isDesktop && (
        <aside
          className="flex-shrink-0 border-r border-gray-800/60 flex flex-col overflow-hidden "
          style={{ width: "224px", height: "100vh" }}
        >
          <SidebarContent />
        </aside>
      )}

      {/* ── Mobile Sidebar Drawer ── */}
      {!isDesktop && (
        <aside
          ref={sidebarRef}
          className="flex flex-col bg-[#0a0a0a] border-r border-gray-800/60"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            width: "256px",
            zIndex: 30,
            transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
            transition: "transform 0.3s ease-in-out",
          }}
        >
          <SidebarContent />
        </aside>
      )}

      {/* ── Main area ── */}
      <div
        className="flex flex-col flex-1 min-w-0"
        style={{ overflow: "hidden", height: "100vh" }}
      >
        {/* Mobile top bar */}
        {!isDesktop && (
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800/60 flex-shrink-0">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-white transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft size={18} />
            </button>
            <span className="text-sm text-gray-400 font-semibold flex-1 truncate">
              {(() => {
                for (const section of docsData) {
                  if (section.id === activeId) return section.title;
                  const sub = section.subsections.find(
                    (s) => s.id === activeId,
                  );
                  if (sub) return sub.title;
                }
                return "Docs";
              })()}
            </span>
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          </div>
        )}

        {/* ── Scrollable content ── */}
        <main
          ref={contentRef}
          className="flex-1 overflow-y-auto dark-scrollbar space-y-12"
          style={{ padding: isDesktop ? "40px" : "24px 16px" }}
        >
          {isDesktop && (
            <div className="mb-10">
              <p className="text-xs uppercase tracking-widest text-[#555] mb-1">
                Documentation
              </p>
              <h1 className="text-3xl font-bold text-white">Editor Guide</h1>
            </div>
          )}
          {docsData.map((section) => (
            <div key={section.id}>
              <div
                ref={(el) => {
                  sectionRefs.current[section.id] = el;
                }}
                className="mb-6"
              >
                <h1 className="text-2xl font-bold text-white tracking-tight">
                  {section.title}
                </h1>
                <div className="mt-3 h-px bg-gradient-to-r from-[#FFF528]/30 via-gray-700 to-transparent" />
              </div>

              <div className="space-y-10">
                {section.subsections.map((sub) => (
                  <div
                    key={sub.id}
                    ref={(el) => {
                      sectionRefs.current[sub.id] = el;
                    }}
                    className="scroll-mt-8"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                      <h2 className="text-lg font-semibold text-white">
                        {sub.title}
                      </h2>
                      {/* {sub.action && (
                      <button
                        onClick={() =>
                          navigate(`/editor/${paramId}/${sub.action.path}`)
                        }
                        className="flex items-center gap-1.5 text-xs text-white bg-[#181A1D] border border-[#2B2E33] hover:border-gray-500 px-3 py-1.5 rounded-md transition-colors flex-shrink-0"
                      >
                        {sub.action.label}
                        <ArrowUpRight size={12} />
                      </button>
                    )} */}
                    </div>

                    <div className="text-sm leading-relaxed space-y-1">
                      {renderContent(sub.content)}
                    </div>

                    {sub.image && (
                      <div className="mt-5 rounded-xl overflow-hidden">
                        <img
                          src={sub.image}
                          alt={sub.title}
                          className="w-full object-contain max-w-5xl mx-auto"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.parentElement.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="h-10" />
        </main>
      </div>
    </div>
  );
};

export default DocsPage;
